import React, { useState, useEffect } from 'react';
import { Users, ChevronDown, CheckCircle } from 'lucide-react';
import { db } from '../../db';
import { syncManager } from '../../services/sync';

interface ClientEntry {
    id: string;
    name: string;
    email: string;
    role?: string;
    status?: string;
    sheetUrl?: string;
}

const ClientSelector = ({ onClientSelected, currentRole }: { onClientSelected: (client: any) => void; currentRole: string }) => {
    const [clients, setClients] = useState<ClientEntry[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchClients = async () => {
            setIsLoading(true);
            try {
                const userMeta = await db.user.toCollection().first();
                const isAdmin = currentRole === 'ADMIN' || userMeta?.role === 'ADMIN';

                // Fetch from Users sheet in HQ Hub
                const rawUsers = await syncManager.fetchTabData('Users');

                if (rawUsers && Array.isArray(rawUsers)) {
                    let filtered = rawUsers.filter(u => u.Client_Email && u.Client_Email !== userMeta?.email);

                    // COACH FILTERING: Only show users assigned to this coach
                                        if (currentRole === 'COACH') {
                                            filtered = filtered.filter(u => 
                                                u.Assigned_Coach_Name === userMeta?.email ||
                                                u.Assigned_Coach_ID === userMeta?.email ||
                                                u.Assigned_Coach_Name === userMeta?.email
                                            );
                                        }

                    const mappedClients = filtered.map(u => ({
                        id: u.UserID || u.Client_Email,
                        name: u.Client_Name || u.Client_Email.split('@')[0],
                        email: u.Client_Email,
                        role: u.User_Role,
                        status: u.Billing_Status || 'Active',
                        sheetUrl: u.Client_Sheet_URL
                    }));
                    setClients(mappedClients);
                } else {
                    console.warn("[ClientSelector] No users found in sheet, using legacy Dexie fallback");
                    // Fallback: check if we have any tabData for Users in Dexie
                    const cached = await db.tabData.where('tabName').equals('Users').first();
                    if (cached?.data && Array.isArray(cached.data)) {
                        setClients(cached.data.map(u => ({
                            id: u.UserID || u.Client_Email,
                            name: u.Client_Name || u.Client_Email,
                            email: u.Client_Email,
                            sheetUrl: u.Client_Sheet_URL
                        })));
                    }
                }
            } catch (e) {
                console.error("Error fetching clients:", e);
            } finally {
                setIsLoading(false);
            }
        };

        if (currentRole === 'ADMIN' || currentRole === 'COACH' || currentRole === 'PARTNER') {
            fetchClients();
        }
    }, [currentRole]);

    if (currentRole === 'CLIENT' || currentRole === 'CUSTOMER') {return null;}

    return (
        <div className="mb-6 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="bg-white rounded-[2rem] border border-slate-200 p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="bg-blue-100 p-3 rounded-2xl text-blue-600">
                        <Users size={24} />
                    </div>
                    <div>
                        <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Managing Client</h3>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
                            {selectedId ? clients.find(c => c.id === selectedId)?.name : 'Select a client to manage'}
                        </p>
                    </div>
                </div>

                <div className="relative group">
                    <select
                        onChange={(e) => {
                            const client = clients.find(c => c.id === e.target.value);
                            setSelectedId(e.target.value);
                            onClientSelected(client);
                        }}
                        className="appearance-none bg-slate-50 border-none rounded-xl px-6 py-3 text-xs font-black uppercase tracking-widest text-slate-600 focus:ring-2 focus:ring-blue-500 transition-all pr-12 cursor-pointer"
                    >
                        <option value="">Choose Client...</option>
                        {clients.map(client => (
                            <option key={client.id} value={client.id}>{client.name}</option>
                        ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
            </div>

            {selectedId && (
                <div className="mt-4 flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-xl border border-green-100 animate-in zoom-in duration-300">
                    <CheckCircle size={14} />
                    <span className="text-[10px] font-black uppercase tracking-widest">
                        Context Switched to: {clients.find(c => c.id === selectedId)?.name}
                    </span>
                </div>
            )}
        </div>
    );
};

export default ClientSelector;